import type { Nullable } from "@msuite/utils/types";
import { cva, type VariantProps } from "class-variance-authority";
import { useNavigate } from "react-router";
import { Button, ButtonGroup } from "../../button";
import type { Disclosure } from "../../hooks";
import { Icon } from "../../icons";
import type { ChildrenProps, ClassNameProps } from "../../types";
import { cn } from "../../utils";

/** Props Interface */
interface RowActionsWrapperBaseProps
	extends ChildrenProps,
		VariantProps<typeof rowActionsWrapper> {}

interface RowActionsWrapperRedirectProps extends RowActionsWrapperBaseProps {
	to?: Nullable<string>;
	type?: "redirect";
	forceBlank?: boolean;
}

interface RowActionsWrapperModalProps extends RowActionsWrapperBaseProps {
	to: null;
	type?: "modal";
	disclosure: Disclosure;
}

type RowActionsWrapperProps = RowActionsWrapperRedirectProps | RowActionsWrapperModalProps;

export function RowActionsWrapper({
	children,
	justify,
	type = "redirect",
	className,
	...props
}: RowActionsWrapperProps & ClassNameProps) {
	/** Classes */
	const classes = rowActionsWrapper({ justify });
	const navigate = useNavigate();

	/** Functions */
	function handleOnNavigate() {
		if (type === "modal") {
			if ("disclosure" in props) {
				props.disclosure.onToggle();
			}
		} else if (type === "redirect") {
			if ("to" in props) {
				if (!props.to) return;
				if ("forceBlank" in props) {
					window.open(props.to, "_blank");
				} else {
					navigate(props.to);
				}
			}
		}
	}

	/** Render */
	return (
		<div className={cn("absolute inset-0 justify-end px-2 w-full h-stack", classes)}>
			<ButtonGroup
				isAttached={false}
				className={cn("gap-2 w-full justify-end", className)}
			>
				{children}
				{(("to" in props && props.to !== null) || type === "modal") && (
					<Button
						size="xxs"
						icon={<Icon name="ArrowRight" />}
						variant="ghost"
						data-cy="row-actions-forward-button"
						onClick={handleOnNavigate}
						isDisabled={(!("to" in props) && type !== "modal") || !props.to}
					/>
				)}
			</ButtonGroup>
		</div>
	);
}

const rowActionsWrapper = cva([], {
	variants: {
		justify: {
			start: "justify-start",
			end: "justify-end",
			center: "justify-center",
		},
	},
	defaultVariants: {
		justify: "end",
	},
});
